import { useGlobalStore } from '@highlight/ipc/react'
import { DEFAULT_EDGE_SENSITIVITY, getEdgeSensitivityConfig } from '@shared/sidepanel/edge-sensitivity'

interface PeekAffordance {
  /** Whether the peek-trigger chevron is enabled at all (settings opt-out). */
  peekTriggerEnabled: boolean
  /**
   * Fraction of screen width over which the chevron slides in on cursor
   * approach. `0` when there's no pre-peek affordance — edge activation or the
   * peek trigger is off, or the sensitivity is too low (levels 1–2). Onboarding
   * widens this further on its own; it's a teaching override, not a setting.
   */
  approachZoneRatio: number
  /** Convenience: there's a non-zero proximity zone keeping the chevron on screen. */
  peekAffordanceActive: boolean
}

/**
 * Single reactive source for the sidepanel peek-trigger affordance gate, derived
 * from the three user settings that govern it. Shared by the trigger itself
 * (`peek-trigger.tsx`) and the `Sidepanel` retract logic (`sidepanel/index.tsx`)
 * so the two can't disagree about when the chevron is on screen.
 */
export function usePeekAffordance(): PeekAffordance {
  const [edgeActivationEnabled] = useGlobalStore<boolean>('settings', 'sidepanel.edge-activation', true)
  const [peekTriggerEnabled] = useGlobalStore<boolean>('settings', 'sidepanel.peek-trigger', true)
  const [edgeSensitivity] = useGlobalStore<number>('settings', 'sidepanel.edge-sensitivity', DEFAULT_EDGE_SENSITIVITY)
  const approachZoneRatio =
    edgeActivationEnabled && peekTriggerEnabled ? getEdgeSensitivityConfig(edgeSensitivity).approachZoneRatio : 0
  return { peekTriggerEnabled, approachZoneRatio, peekAffordanceActive: approachZoneRatio > 0 }
}
